import { type ApiClient, newIdempotencyKey } from './client.ts';
import { readSse } from './sse.ts';
import type {
  BlobDownload,
  BlobSpec,
  Catalog,
  CheckoutRequest,
  CommitBlobsResponse,
  DeleteAccountResult,
  Me,
  Org,
  PrepareBlobsResponse,
  ProjectPush,
  ProjectRecord,
  PullResponse,
  PushResult,
  Render,
  RenderPartial,
  RenderRequest,
  Schemas,
} from './types.ts';

/** One typed method per operation in contracts/openapi.yaml (operationId in comments). */
export class BetterLightsApi {
  constructor(readonly client: ApiClient) {}

  /** getHealth */
  health() {
    return this.client.request<{ status: string }>('GET', '/healthz', { auth: false });
  }

  /** getMe */
  me(signal?: AbortSignal) {
    return this.client.request<Me>('GET', '/v1/me', { signal });
  }

  /** deleteMe */
  deleteMe(body: { appleAuthorizationCode?: string } = {}) {
    return this.client.request<DeleteAccountResult>('DELETE', '/v1/me', { body, retry: false });
  }

  /** createAttestChallenge (web: the Turnstile widget's cData) */
  attestChallenge() {
    return this.client.request<Schemas['AttestChallenge']>('POST', '/v1/attest/challenge', { retry: true });
  }

  /** claimTrial (once per verified device and account) */
  claimTrial(body: Schemas['TrialRequest']) {
    return this.client.request<Schemas['TrialResult']>('POST', '/v1/trial', { body, retry: false });
  }

  /** syncPull */
  pull(cursor: string | null, limit = 200, signal?: AbortSignal) {
    return this.client.request<PullResponse>('GET', '/v1/sync/pull', {
      query: { cursor: cursor ?? undefined, limit },
      clientSchema: true,
      signal,
    });
  }

  /** pushProject (whole document, server 3-way merges). */
  push(projectId: string, body: ProjectPush, idempotencyKey: string = newIdempotencyKey()) {
    return this.client.request<PushResult>('PUT', `/v1/projects/${encodeURIComponent(projectId)}`, {
      body,
      clientSchema: true,
      idempotencyKey,
    });
  }

  /** deleteProject (tombstone, restorable for 30 days) */
  deleteProject(projectId: string, baseRev: number) {
    return this.client.request<PushResult>('DELETE', `/v1/projects/${encodeURIComponent(projectId)}`, {
      query: { baseRev },
    });
  }

  /** restoreProject */
  restoreProject(projectId: string) {
    return this.client.request<ProjectRecord>('POST', `/v1/projects/${encodeURIComponent(projectId)}/restore`, {
      retry: true,
    });
  }

  /** prepareBlobs (`render` inputs are allowed on every plan; `sync` needs the sync feature) */
  prepareBlobs(scope: string, blobs: BlobSpec[], purpose: 'sync' | 'render' = 'sync') {
    return this.client.request<PrepareBlobsResponse>('POST', '/v1/blobs/prepare', {
      body: { scope, purpose, blobs },
      retry: true,
    });
  }

  /** commitBlobs */
  commitBlobs(scope: string, refs: string[]) {
    return this.client.request<CommitBlobsResponse>('POST', '/v1/blobs/commit', { body: { scope, refs }, retry: true });
  }

  /** getBlob (presigned download URL) */
  blobUrl(ref: string, scope: string) {
    return this.client.request<BlobDownload>('GET', `/v1/blobs/${encodeURIComponent(ref)}`, { query: { scope } });
  }

  /** createRender (Idempotency-Key required; reused on retries) */
  createRender(body: RenderRequest, idempotencyKey: string) {
    return this.client.request<Render>('POST', '/v1/renders', { body, idempotencyKey });
  }

  /** getRender */
  getRender(id: string, signal?: AbortSignal) {
    return this.client.request<Render>('GET', `/v1/renders/${id}`, { signal });
  }

  /** setRenderFeedback: thumbs up / down on a render image (`none` clears it) */
  setRenderFeedback(aiResultId: string, rating: 'up' | 'down' | 'none') {
    return this.client.request<void>('PUT', '/v1/render-feedback', { body: { aiResultId, rating } });
  }

  /** cancelRender */
  cancelRender(id: string) {
    return this.client.request<Render>('DELETE', `/v1/renders/${id}`);
  }

  /** renderEvents (SSE): `render` and `partial` events until a terminal status. */
  renderEvents(
    id: string,
    on: { render: (r: Render) => void; partial: (p: RenderPartial) => void },
    signal: AbortSignal,
  ): Promise<void> {
    return readSse(
      this.client.url(`/v1/renders/${id}/events`),
      () => this.client.headers({}),
      (e) => {
        if (e.event === 'partial') on.partial(JSON.parse(e.data) as RenderPartial);
        else if (e.event === 'render') {
          const r = JSON.parse(e.data) as Render;
          on.render(r);
          if (r.status === 'succeeded' || r.status === 'failed' || r.status === 'canceled') return 'stop';
        }
        return undefined;
      },
      signal,
    );
  }

  /** createOrg */
  createOrg(name: string) {
    return this.client.request<Org>('POST', '/v1/orgs', { body: { name }, retry: false });
  }

  /** getOrg */
  org(id: string) {
    return this.client.request<Org>('GET', `/v1/orgs/${id}`);
  }

  /** getCatalog */
  catalog(orgId: string) {
    return this.client.request<Catalog>('GET', `/v1/orgs/${orgId}/catalog`);
  }

  /** createCheckout (Stripe subscription: Starter / Pro / Max / Team seats) */
  checkout(body: CheckoutRequest) {
    return this.client.request<{ url: string }>('POST', '/v1/billing/checkout', { body, retry: false });
  }

  /** Stripe one-time Checkout for a credit pack (Free too; `orgId` = company wallet). */
  checkoutPack(body: Schemas['PackCheckoutRequest']) {
    return this.client.request<{ url: string }>('POST', '/v1/billing/checkout/pack', { body, retry: false });
  }

  /** devGrant — DEV ONLY (server DEV_ENDPOINTS=1): plan and/or credits for local testing. */
  devGrant(body: Schemas['DevGrantRequest']) {
    return this.client.request<Schemas['DevGrantResult']>('POST', '/v1/dev/grant', { body, retry: false });
  }

  /** redeemBetaCode — a beta invite code (case-insensitive, spaces and dashes ignored) → beta grant */
  redeemBeta(code: string) {
    return this.client.request<Schemas['BetaGrant']>('POST', '/v1/beta/redeem', { body: { code }, retry: false });
  }

  /** createBillingPortal (Stripe Customer Portal; orgId for a Team plan) */
  billingPortal(body: { orgId?: string; returnUrl?: string } = {}) {
    return this.client.request<{ url: string }>('POST', '/v1/billing/portal', { body, retry: false });
  }

  /** getPlans (public plan catalog with prices, packs, trial) */
  plans() {
    return this.client.request<Schemas['PlanCatalog']>('GET', '/v1/plans', { auth: false });
  }

  /** getPersonalCatalog */
  meCatalog() {
    return this.client.request<Catalog>('GET', '/v1/me/catalog');
  }

  /** putPersonalCatalog (merged by the server) */
  putMeCatalog(baseRev: number, doc: Record<string, unknown>) {
    return this.client.request<Catalog>('PUT', '/v1/me/catalog', { body: { baseRev, doc }, idempotencyKey: true });
  }

  /** putCatalog (org; admin+) */
  putCatalog(orgId: string, baseRev: number, doc: Record<string, unknown>) {
    return this.client.request<Catalog>('PUT', `/v1/orgs/${orgId}/catalog`, {
      body: { baseRev, doc },
      idempotencyKey: true,
    });
  }

  /** updateOrgSettings (daily credit cap; null = server default) */
  updateOrgSettings(orgId: string, dailyCreditCap: number | null) {
    return this.client.request<Schemas['OrgSettings']>('PUT', `/v1/orgs/${orgId}/settings`, {
      body: { dailyCreditCap },
      idempotencyKey: true,
    });
  }

  /** getOrgUsage */
  orgUsage(orgId: string) {
    return this.client.request<Schemas['OrgUsage']>('GET', `/v1/orgs/${orgId}/usage`);
  }

  /** listMembers */
  members(orgId: string) {
    return this.client.request<{ members: Schemas['Membership'][] }>('GET', `/v1/orgs/${orgId}/members`);
  }

  /** updateMember (role) */
  updateMember(orgId: string, userId: string, role: Schemas['Role']) {
    return this.client.request<Schemas['Membership']>('PATCH', `/v1/orgs/${orgId}/members/${userId}`, {
      body: { role },
      idempotencyKey: true,
    });
  }

  /** removeMember (or leave) */
  removeMember(orgId: string, userId: string) {
    return this.client.request<void>('DELETE', `/v1/orgs/${orgId}/members/${userId}`);
  }

  /** listInvites */
  invites(orgId: string) {
    return this.client.request<{ invites: Schemas['Invite'][] }>('GET', `/v1/orgs/${orgId}/invites`);
  }

  /** createInvite */
  createInvite(orgId: string, email: string, role: 'admin' | 'member' | 'viewer') {
    return this.client.request<Schemas['Invite']>('POST', `/v1/orgs/${orgId}/invites`, {
      body: { email, role },
      retry: false,
    });
  }

  /** revokeInvite */
  revokeInvite(orgId: string, inviteId: string) {
    return this.client.request<void>('DELETE', `/v1/orgs/${orgId}/invites/${inviteId}`);
  }

  /** transferOwnership (owners only; the current owner becomes an admin) */
  transferOwnership(orgId: string, userId: string) {
    return this.client.request<Org>('POST', `/v1/orgs/${orgId}/transfer`, { body: { userId }, retry: false });
  }

  /** resendInvite (fresh token and expiry; the new token is returned once) */
  resendInvite(orgId: string, inviteId: string) {
    return this.client.request<Schemas['Invite']>('POST', `/v1/orgs/${orgId}/invites/${inviteId}/resend`, {
      retry: false,
    });
  }

  /** acceptInvite */
  acceptInvite(token: string) {
    return this.client.request<Schemas['Membership']>('POST', '/v1/invites/accept', { body: { token }, retry: false });
  }

  /** moveProject (personal ↔ org; keeps id and history) */
  moveProject(projectId: string, toScope: string) {
    return this.client.request<ProjectRecord>('POST', `/v1/projects/${encodeURIComponent(projectId)}/move`, {
      body: { toScope },
      idempotencyKey: true,
    });
  }
}
